// Copyright (c) Ping. All rights reserved.

import type { AuthFlowAction, AuthFlowMachine, AuthFlowState } from './authFlowState';

/**
 * Pure reducer for the auth-flow state machine.
 *
 * Invariants:
 *   1. Reducer is total: every (state, action) pair returns a machine.
 *   2. Reducer is pure: no IO, no logging, no `Date.now()`.
 *   3. Action discriminants match the Wave-3 cross-platform contract
 *      verbatim (see `_NAMING_CONTRACT-WAVE3.md`).
 *
 * Unhandled (state, action) combinations are no-ops — the machine
 * stays put. Callers should never dispatch out-of-order actions; UI
 * affordances are the gate.
 *
 * invite-flow Wave-3A: lifted verbatim from
 * `apps/desktop/src/auth/authFlowReducer.ts` so both apps share the
 * same transition graph.
 */
export function authFlowReducer(machine: AuthFlowMachine, action: AuthFlowAction): AuthFlowMachine {
  const { state, context } = machine;

  switch (action.type) {
    case 'welcomeChoseExisting':
      if (state.kind !== 'welcome') return machine;
      return {
        state: { kind: 'chooseMethod' },
        context: { ...context, authMode: 'signIn' },
      };

    case 'welcomeChoseNew':
      if (state.kind !== 'welcome') return machine;
      return {
        state: { kind: 'chooseMethod' },
        context: { ...context, authMode: 'register' },
      };

    case 'methodChoseEmail':
      if (state.kind !== 'chooseMethod') return machine;
      return { state: { kind: 'enteringEmail' }, context };

    case 'methodChosePhone':
      if (state.kind !== 'chooseMethod') return machine;
      return { state: { kind: 'enteringPhone' }, context };

    case 'emailValidated':
      if (state.kind !== 'enteringEmail') return machine;
      return {
        state: {
          kind: 'enteringCode',
          method: 'email',
          contact: { kind: 'email', value: action.email },
        },
        context,
      };

    case 'phoneValidated':
      if (state.kind !== 'enteringPhone') return machine;
      return {
        state: {
          kind: 'enteringCode',
          method: 'phone',
          contact: { kind: 'phone', e164: action.e164 },
        },
        context,
      };

    case 'codeRequested':
      // Allow the coordinator to attach the backend-issued challengeId
      // either while still on the contact-entry step or after the
      // transition to `enteringCode`. Either way, only the context
      // changes.
      return { state, context: { ...context, challengeId: action.challengeId } };

    case 'codeVerified': {
      if (state.kind !== 'enteringCode') return machine;
      if (context.authMode === 'signIn') {
        // sign-in finishes after verify; the coordinator must still
        // dispatch `authenticated` once it has the AuthenticatedUser.
        return machine;
      }
      // register path continues into the post-verify chain
      return {
        state: { kind: 'enteringPassword', authMode: 'register' },
        context,
      };
    }

    case 'passwordSet':
      if (state.kind !== 'enteringPassword') return machine;
      return { state: { kind: 'enteringSaveLoginChoice' }, context };

    case 'saveLoginChoiceMade':
      if (state.kind !== 'enteringSaveLoginChoice') return machine;
      return {
        state: { kind: 'enteringDisplayName' },
        context: { ...context, saveLogin: action.saveLogin },
      };

    case 'displayNameSet':
      if (state.kind !== 'enteringDisplayName') return machine;
      return {
        state: { kind: 'acceptingPrivacy' },
        context: { ...context, displayName: action.displayName },
      };

    case 'privacyAccepted':
      if (state.kind !== 'acceptingPrivacy') return machine;
      return { state: { kind: 'importingContacts' }, context };

    case 'importContactsCompleted':
    case 'importContactsSkipped':
      if (state.kind !== 'importingContacts') return machine;
      return { state: { kind: 'findingCompany' }, context };

    case 'findCompanyCompleted':
    case 'findCompanySkipped':
      if (state.kind !== 'findingCompany') return machine;
      // The coordinator is responsible for dispatching `authenticated`
      // immediately after the user finishes/skips findCompany. This
      // intermediate `done`-marker state is omitted; the next
      // `authenticated` action moves the machine to its terminal state.
      return machine;

    case 'authenticated':
      return { state: { kind: 'done', user: action.user }, context };

    case 'back':
      return previousStepFor(state, machine);

    default: {
      const _exhaustive: never = action;
      void _exhaustive;
      return machine;
    }
  }
}

function previousStepFor(state: AuthFlowState, machine: AuthFlowMachine): AuthFlowMachine {
  const { context } = machine;
  switch (state.kind) {
    case 'welcome':
    case 'done':
      return machine;
    case 'chooseMethod':
      return { state: { kind: 'welcome' }, context };
    case 'enteringEmail':
    case 'enteringPhone':
      return { state: { kind: 'chooseMethod' }, context };
    case 'enteringCode':
      return state.method === 'email'
        ? { state: { kind: 'enteringEmail' }, context }
        : { state: { kind: 'enteringPhone' }, context };
    case 'enteringPassword':
      return {
        state: { kind: 'enteringCode', method: 'phone', contact: { kind: 'phone', e164: '' } },
        context,
      };
    case 'enteringSaveLoginChoice':
      return { state: { kind: 'enteringPassword', authMode: 'register' }, context };
    case 'enteringDisplayName':
      return { state: { kind: 'enteringSaveLoginChoice' }, context };
    case 'acceptingPrivacy':
      return { state: { kind: 'enteringDisplayName' }, context };
    case 'importingContacts':
      return { state: { kind: 'acceptingPrivacy' }, context };
    case 'findingCompany':
      return { state: { kind: 'importingContacts' }, context };
    default: {
      const _exhaustive: never = state;
      void _exhaustive;
      return machine;
    }
  }
}
